/**
 * The graph artifact's shape, and the indexed view of it.
 *
 * The map that read narration and flows here is gone (DECISIONS #63); what
 * still needs the graph is the live-infra panel, which joins queue and
 * service entities to what the sampler observed. Consumers read ONLY these
 * artifact JSON shapes (served verbatim by
 * `GET /api/repos/{rid}/artifacts/{kind}`) plus the frozen contract types in
 * `web/src/api/types.ts`. Shapes mirror the engine writers:
 * `src/loremap/extract.py` (graph) and `infra.py` (entities, entity_edges,
 * findings).
 *
 * Indexing happens once inside a TanStack Query `select` (module-level
 * selector functions keep the reference stable), so every consumer shares
 * the parsed maps by reference (ARCHITECTURE §4.3 performance plan).
 */

import type { UseQueryResult } from '@tanstack/react-query';

import { useArtifact } from '../api/queries';
import type { Ulid } from '../api/types';

// ---------------------------------------------------------------------------
// Graph artifact (engine extract.py; v2 adds entities/entity_edges/findings)
// ---------------------------------------------------------------------------

export interface GraphFileEntry {
  path: string;
  loc: number;
  size: number;
}

/** Surface entries vary by kind; these fields cover every detector. */
export interface GraphSurface {
  kind: string;
  name?: string;
  method?: string;
  path?: string;
  file?: string;
  line?: number;
  [key: string]: unknown;
}

export interface GraphNode {
  name: string;
  depth: number;
  parent: string | null;
  children: string[];
  content_hash: string;
  file_count: number;
  loc: number;
  languages: Record<string, number>;
  files: GraphFileEntry[];
  direct_file_count: number;
  surfaces: GraphSurface[];
  last_commit: string;
  recent_commits: string[];
  tickets: unknown[];
}

export interface GraphImportEdge {
  from: string;
  to: string;
  count: number;
  kind: string;
}

export interface EntitySource {
  path: string;
  line?: number;
}

/** Graph-v2 infra entity (infra.py). Ids are namespaced: svc:/queue:/sched:/ext: */
export interface GraphEntity {
  id: string;
  kind: 'service' | 'queue' | 'schedule' | 'external';
  label: string;
  source: EntitySource | null;
  attrs: Record<string, unknown>;
  content_hash?: string;
}

/** from/to carry explicit namespaces; dir nodes appear as "dir:<path>". */
export interface GraphEntityEdge {
  from: string;
  to: string;
  /** consumes, produces, runs, triggers; the server may add more, so the type stays open */
  kind: string;
  source?: EntitySource | null;
}

export interface GraphFinding {
  kind: string;
  entity_id: string;
  message: string;
  evidence?: string[];
}

export interface GraphMeta {
  project_name?: string;
  surface_labels?: Record<string, string>;
  lanes?: Array<{ id: string; label: string }>;
  [key: string]: unknown;
}

export interface GraphArtifact {
  version: number;
  generated_at_sha: string;
  meta: GraphMeta;
  root: string;
  nodes: Record<string, GraphNode>;
  edges: GraphImportEdge[];
  entities?: Record<string, GraphEntity>;
  entity_edges?: GraphEntityEdge[];
  findings?: GraphFinding[];
}

// ---------------------------------------------------------------------------
// Indexed views (built once in query `select`)
// ---------------------------------------------------------------------------

export interface GraphIndex {
  artifact: GraphArtifact;
  /** dir ids sorted; root excluded */
  dirIds: string[];
  /** graph-v2 entities by id (empty map on v1) */
  entities: Map<string, GraphEntity>;
  entityEdges: GraphEntityEdge[];
  findings: GraphFinding[];
  /** entity id → findings touching it */
  findingsByEntity: Map<string, GraphFinding[]>;
  /** dir id → dirs directly under it (from `children`) */
  lanes: Array<{ id: string; label: string }>;
  surfaceLabels: Record<string, string>;
}

export function indexGraph(artifact: GraphArtifact): GraphIndex {
  const entities = new Map<string, GraphEntity>();
  for (const [id, entity] of Object.entries(artifact.entities ?? {})) {
    entities.set(id, entity);
  }
  const findings = artifact.findings ?? [];
  const findingsByEntity = new Map<string, GraphFinding[]>();
  for (const finding of findings) {
    const list = findingsByEntity.get(finding.entity_id) ?? [];
    list.push(finding);
    findingsByEntity.set(finding.entity_id, list);
  }
  return {
    artifact,
    dirIds: Object.keys(artifact.nodes)
      .filter((id) => id !== artifact.root)
      .sort(),
    entities,
    entityEdges: artifact.entity_edges ?? [],
    findings,
    findingsByEntity,
    lanes: artifact.meta.lanes ?? [],
    surfaceLabels: artifact.meta.surface_labels ?? {},
  };
}

// ---------------------------------------------------------------------------
// Hooks — thin wrappers over useArtifact with stable select references
// ---------------------------------------------------------------------------

export function useGraph(projectId: Ulid | undefined): UseQueryResult<GraphIndex> {
  return useArtifact<GraphArtifact, GraphIndex>(projectId, 'graph', indexGraph);
}

